import { defineAsyncComponent } from 'vue'

import EmojiReactions from 'src/components/emoji_reactions/emoji_reactions.vue'
import MentionLink from 'src/components/mention_link/mention_link.vue'
import MentionsLine from 'src/components/mentions_line/mentions_line.vue'
import NameWithEmoji from 'src/components/name_with_emoji/name_with_emoji.vue'
import PostStatusForm from 'src/components/post_status_form/post_status_form.vue'
import RepeatBadge from 'src/components/repeat_badge/repeat_badge.vue'
import StatusActionButtons from 'src/components/status_action_buttons/status_action_buttons.vue'
import StatusContent from 'src/components/status_content/status_content.vue'
import StatusPopover from 'src/components/status_popover/status_popover.vue'
import StatusTranslation from 'src/components/status_translation/status_translation.vue'
import Timeago from 'src/components/timeago/timeago.vue'
import UserAvatar from 'src/components/user_avatar/user_avatar.vue'
import UserAvatarLink from 'src/components/user_avatar_link/user_avatar_link.vue'
import UserLink from 'src/components/user_link/user_link.vue'
import { muteFilterHits } from '../../services/status_parser/status_parser.js'
import {
  highlightClass,
  highlightStyle,
} from '../../services/user_highlighter/user_highlighter.js'

import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useScrobblesStore } from 'src/stores/scrobbles.js'
import { allMuteFilters } from 'src/stores/server_filters.js'
import { useStatusesStore } from 'src/stores/statuses.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useUserHighlightStore } from 'src/stores/user_highlight.js'
import { useUsersStore } from 'src/stores/users.js'

import { leadingMentionUrl } from 'src/services/leading_mention/leading_mention.js'
import { composerMode, quoteUrls } from 'src/services/quoting/quoting.js'
import { formatScheduleTime } from 'src/services/scheduling/scheduling.js'
import { muteLabel, muteReasons } from 'src/services/status_mute/status_mute.js'
import { visibilityIcon } from 'src/services/status_visibility/status_visibility.js'
import { displayName } from 'src/services/user_display/user_display.js'

const Status = {
  name: 'Status',
  components: {
    NameWithEmoji,
    StatusTranslation,
    PostStatusForm,
    UserAvatar,
    Timeago,
    StatusPopover,
    RepeatBadge,
    EmojiReactions,
    StatusContent,
    MentionLink,
    MentionsLine,
    UserAvatarLink,
    UserLink,
    Quote: defineAsyncComponent(() => import('src/components/quote/quote.vue')),
    StatusActionButtons,
  },
  props: {
    statusId: String,
    statusoid: Object,

    expandable: Boolean,
    focused: Boolean,
    compact: Boolean,
    isPreview: Boolean,
    noHeading: Boolean,
    inlineExpanded: Boolean,
    inProfile: Boolean,
    inConversation: Boolean,
    // where the post is shown, for the server's word filters: 'home',
    // 'public' or 'notifications'; open conversations are 'thread'
    filterPlace: { type: String, default: null },

    profileUserId: String,
    ignoreMute: Boolean,
  },
  emits: ['goto', 'toggleExpanded', 'suspendableStateChange'],
  data() {
    return {
      // the form under the post: null, 'reply' or 'quote'
      replyMode: null,
      unmuted: false,
      mediaPlaying: new Set(),
      error: null,
      headTailLinks: null,
    }
  },
  provide() {
    return {
      // for the reply arrow in the text
      gotoReplyParent: () => this.gotoReplyParent(),
    }
  },
  created() {
    useScrobblesStore().getLatestScrobble(this.status.user.id)
  },
  // built as the chosen post (a big thread builds its posts in steps): the
  // focused watcher sees no change, so scroll here
  mounted() {
    if (this.focused) this.scrollIfFocused(true)
  },
  computed: {
    // Whatever we're given to work with
    status() {
      return this.statusoid ?? useStatusesStore().allStatuses.get(this.statusId)
    },
    // Status repeated
    repeatedStatus() {
      if (this.status.retweeted_status === undefined) return undefined
      return useStatusesStore().allStatuses.get(this.status.retweeted_status.id)
    },
    mainStatus() {
      if (this.isRepeat) {
        return this.repeatedStatus
      } else {
        return this.status
      }
    },
    repeater() {
      return useUsersStore().findUser(this.status.user.id)
    },
    user() {
      return useUsersStore().findUser(this.mainStatus.user.id)
    },
    userClass() {
      return highlightClass(this.user)
    },
    isDeleted() {
      return this.status.deleted
    },
    favoritedBy() {
      return useStatusesStore().favs.get(this.mainStatus.id) ?? new Set()
    },
    repeatedBy() {
      return useStatusesStore().repeats.get(this.mainStatus.id) ?? new Set()
    },
    userStyle() {
      if (this.noHeading) return
      return highlightStyle(useUserHighlightStore().get(this.user.screen_name))
    },
    replyProfileLink() {
      if (this.isReply) {
        const user = useUsersStore().findUser(
          this.mainStatus.in_reply_to_user_id,
        )

        // User referenced in post might not be yet present in store
        // since their data is not included in status data, just the id
        return user?.statusnet_profile_url
      }
    },
    isRepeat() {
      return !!this.repeatedStatus
    },
    muteFilterHits() {
      return muteFilterHits(
        allMuteFilters(
          useSyncConfigStore().prefsStorage.simple.muteFilters,
          this.inConversation ? 'thread' : this.filterPlace,
        ),
        this.status,
      )
    },
    botStatus() {
      return this.status.user.actor_type === 'Service'
    },
    sensitiveStatus() {
      return this.status.nsfw
    },
    mentionsLine() {
      if (!this.headTailLinks) return []
      const writtenSet = new Set(
        this.headTailLinks.writtenMentions.map((_) => _.url),
      )
      return this.mainStatus.attentions
        .filter((attn) => {
          // no reply user
          return (
            attn.id !== this.mainStatus.in_reply_to_user_id &&
            // no self-replies
            attn.statusnet_profile_url !==
              this.mainStatus.user.statusnet_profile_url &&
            // don't include if mentions is written
            !writtenSet.has(attn.statusnet_profile_url)
          )
        })
        .map((attn) => ({
          url: attn.statusnet_profile_url,
          content: attn.screen_name,
          userId: attn.id,
        }))
    },
    hasMentionsLine() {
      return this.mentionsLine.length > 0
    },
    muteReasons() {
      return muteReasons({
        userMuted: this.userIsMuted,
        threadMuted: this.mainStatus.thread_muted,
        filterHits: this.muteFilterHits,
        bot: this.muteBotStatuses && this.botStatus,
        sensitive: this.muteSensitiveStatuses && this.sensitiveStatus,
      })
    },
    muteLocalized() {
      return muteLabel(this.muteReasons, this.muteFilterHits, this.$t)
    },
    muted() {
      if (this.ignoreMute) return false
      if (this.status.user.id === this.currentUser?.id) return false
      return !this.unmuted && !this.shouldNotMute && this.muteReasons.length > 0
    },
    userIsMuted() {
      if (!this.currentUser) return false
      if (this.user === this.currentUser) return false
      if (this.repeater === this.currentUser) return false
      const relationship = useUsersStore().relationship(this.user.id)
      const relationshipRepeat = useUsersStore().relationship(this.repeater?.id)
      return (
        (this.status.muted && !this.status.thread_muted) ||
        // Reprööt of a muted post according to BE
        (this.repeatedStatus?.muted && !this.repeatedStatus.thread_muted) ||
        // Muted or blocked user (as a quote hides them)
        relationship.muting ||
        relationship.blocking ||
        // Muted user of a reprööt
        relationshipRepeat?.muting
      )
    },
    shouldNotMute() {
      if (this.ignoreMute) return true
      if (this.focused) return true
      const { reblog } = this.mainStatus
      return (
        ((this.inProfile &&
          // Don't mute user's posts on user timeline (except reblogs)
          ((!reblog && this.mainStatus.user.id === this.profileUserId) ||
            // Same as above but also allow self-reblogs
            reblog?.user.id === this.profileUserId)) ||
          // Don't mute statuses in muted conversation when said conversation is opened
          (this.inConversation && this.mainStatus.thread_muted)) &&
        // No excuses if post has muted words
        !this.muteFilterHits.length > 0
      )
    },
    hideMutedUsers() {
      return this.mergedConfig.hideMutedPosts
    },
    hideMutedThreads() {
      return this.mergedConfig.hideMutedThreads
    },
    hideFilteredStatuses() {
      return this.mergedConfig.hideFilteredStatuses
    },
    hideWordFilteredPosts() {
      return this.mergedConfig.hideWordFilteredPosts
    },
    hideStatus() {
      return (
        !this.shouldNotMute &&
        ((this.muted && this.hideFilteredStatuses) ||
          (this.userIsMuted && this.hideMutedUsers) ||
          (this.status.thread_muted && this.hideMutedThreads) ||
          (this.muteFilterHits.length > 0 && this.hideWordFilteredPosts) ||
          this.muteFilterHits.some((x) => x.hide))
      )
    },
    isReply() {
      return !!(
        this.mainStatus.in_reply_to_status_id &&
        this.mainStatus.in_reply_to_user_id
      )
    },
    // A reply whose text starts with the replied-to person, in the full
    // post view: an arrow at that mention replaces the "Reply to" line.
    // Kept as a line: a hidden parent, text behind a warning.
    replyArrow() {
      const status = this.mainStatus
      if (!this.isReply) return null
      if (!status.parent_visible || status.summary) return null
      const url =
        status.attentions?.find((a) => a.id === status.in_reply_to_user_id)
          ?.statusnet_profile_url ??
        useUsersStore().findUser(status.in_reply_to_user_id)
          ?.statusnet_profile_url
      if (!url || leadingMentionUrl(status.raw_html) !== url) return null
      // a hover preview gets only the icon: no popover in a popover
      return this.isPreview
        ? { statusId: status.in_reply_to_status_id, plain: true }
        : { statusId: status.in_reply_to_status_id }
    },
    showReplyLine() {
      return this.isReply && !this.replyArrow
    },
    replyToName() {
      if (this.mainStatus.in_reply_to_screen_name) {
        return this.mainStatus.in_reply_to_screen_name
      } else {
        const user = useUsersStore().findUser(
          this.mainStatus.in_reply_to_user_id,
        )
        return user?.screen_name_ui
      }
    },
    tags() {
      return [...this.status.tags]
        .filter((tagObj) => Object.hasOwn(tagObj, 'name'))
        .map((tagObj) => tagObj.name)
        .join(' ')
    },
    muteBotStatuses() {
      return this.mergedConfig.muteBotStatuses
    },
    muteSensitiveStatuses() {
      return this.mergedConfig.muteSensitiveStatuses
    },
    currentUser() {
      return useUsersStore().currentUser
    },
    mergedConfig() {
      return useMergedConfigStore().mergedConfig
    },
    isSuspendable() {
      return !this.replyMode && this.mediaPlaying.size === 0
    },
    // a self-deleting post: when it goes away
    expiryText() {
      return this.$t('status.deletes_at', {
        time: formatScheduleTime(this.mainStatus.expires_at, this.$i18n.locale),
      })
    },
    visibilityLocalized() {
      return this.$i18n.t('general.scope_in_timeline.' + this.status.visibility)
    },
    isEdited() {
      return this.mainStatus.edited_at !== null
    },
    editingAvailable() {
      return useInstanceCapabilitiesStore().editingAvailable
    },
    quoteId() {
      return this.mainStatus.quote_id
    },
    // its page, else its id: the text's "RE: <link>" is taken out by either
    quoteUrl() {
      return quoteUrls(this.mainStatus)[0]
    },
    quoteVisible() {
      return this.mainStatus.quote_visible
    },
    scrobblePresent() {
      if (this.mergedConfig.hideScrobbles) return false
      if (!this.status.user?.latestScrobble) return false
      const value = this.mergedConfig.hideScrobblesAfter.match(/\d+/gs)[0]
      const unit = this.mergedConfig.hideScrobblesAfter.match(/\D+/gs)[0]
      let multiplier = 60 * 1000 // minutes is smallest unit
      switch (unit) {
        case 'm':
          break
        case 'h':
          multiplier *= 60 // hour
          break
        case 'd':
          multiplier *= 60 // hour
          multiplier *= 24 // day
          break
      }
      const maxAge = Number(value) * multiplier
      const createdAt = Date.parse(this.status.user.latestScrobble.created_at)
      const age = Date.now() - createdAt
      if (age > maxAge) return false
      return this.status.user.latestScrobble.artist
    },
    scrobble() {
      return this.status.user?.latestScrobble
    },
  },
  methods: {
    nameOf: displayName,
    visibilityIcon,
    clearError() {
      this.error = undefined
    },
    // Reply and Quote open the form, switch it, or (pressed again) close it
    setReplyMode(pressed) {
      const next = composerMode(this.replyMode, pressed)
      if (next === null) {
        // This emits 'close-accepted' if successful
        // which in turn calls closeReplyForm()
        this.$refs.postStatusForm.requestClose()
      } else {
        this.replyMode = next
      }
    },
    toggleReplyForm() {
      this.setReplyMode('reply')
    },
    toggleQuoteForm() {
      this.setReplyMode('quote')
    },
    closeReplyForm() {
      this.replyMode = null
    },
    gotoOriginal(id) {
      if (this.inConversation) {
        this.$emit('goto', id)
      }
    },
    // the reply sign: in a thread, jump to the replied-to post; elsewhere
    // open it, as a click in its hover preview does
    gotoReplyParent() {
      const id = this.mainStatus.in_reply_to_status_id
      if (this.inConversation) {
        this.gotoOriginal(id)
      } else {
        this.$router.push({ name: 'conversation', params: { id } })
      }
    },
    toggleExpanded() {
      this.$emit('toggleExpanded')
    },
    toggleMute() {
      this.unmuted = !this.unmuted
    },
    addMediaPlaying(id) {
      this.mediaPlaying.add(id)
    },
    removeMediaPlaying(id) {
      this.mediaPlaying.delete(id)
    },
    setHeadTailLinks(headTailLinks) {
      this.headTailLinks = headTailLinks
    },
    scrollIfFocused(focused) {
      if (!focused || this.$el.getBoundingClientRect == null) return
      this.placeInView()
      // once more after the next frame is laid out: posts above that were
      // not drawn yet (content-visibility in a thread) take their real
      // height in that layout, which comes after a first frame callback
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.placeInView()),
      )
    },
    placeInView() {
      if (!this.$el?.isConnected) return
      const rect = this.$el.getBoundingClientRect()
      if (rect.top < 100) {
        // Post is above screen, match its top to screen top
        window.scrollBy(0, rect.top - 100)
      } else if (rect.height >= window.innerHeight - 50) {
        // Post we want to see is taller than screen so match its top to screen top
        window.scrollBy(0, rect.top - 100)
      } else if (rect.bottom > window.innerHeight - 50) {
        // Post is below screen, match its bottom to screen bottom
        window.scrollBy(0, rect.bottom - window.innerHeight + 50)
      }
    },
  },
  watch: {
    // after the page has updated: a reply opened deep in a thread gets its
    // parents drawn above it in the same update, which moves it down
    focused: {
      handler(id) {
        this.scrollIfFocused(id)
      },
      flush: 'post',
    },
    'mainStatus.repeat_num': function (num) {
      // refetch repeats when repeat_num is changed in any way
      if (this.focused && this.repeatedBy.size !== num) {
        useStatusesStore().fetchRepeats(this.mainStatus.id)
      }
    },
    'mainStatus.fave_num': function (num) {
      // refetch favs when fave_num is changed in any way
      if (this.focused && this.favoritedBy.size !== num) {
        useStatusesStore().fetchFavs(this.mainStatus.id)
      }
    },
    isSuspendable: function (suspend) {
      this.$emit('suspendableStateChange', { id: this.status.id, suspend })
    },
  },
}

export default Status
